import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import MobileSearchField from './MobileSearchField';
import useVisualViewportSheetStyle from '../../hooks/useVisualViewportSheetStyle';
import { blurMobileSearchInput, nudgeMobileViewportScaleAfterKeyboard } from '../../utils/mobileSearchInput';

/**
 * Full-width bottom sheet for picking a player on mobile: pins above the virtual keyboard
 * (`useVisualViewportSheetStyle`), locks body scroll while open, and gives search + results a
 * single scroll region so there is no "scroll the list, then scroll the modal" dead zone.
 *
 * Search state is controlled by the caller (`searchTerm` / `onSearchChange`) so callers can
 * filter into sections (e.g. "playing" vs "waiting") instead of being locked into a flat list —
 * pass the filtered rows/sections as `children`.
 */
const PlayerPickerSheet = ({
  isOpen,
  onClose,
  title,
  subtitle,
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search players…',
  emptyMessage = 'No players found',
  hasResults = true,
  /** Pinned content rendered between search and the scrollable results (e.g. an "add new guest" action). */
  belowSearch,
  children,
  className = '',
}) => {
  const searchInputRef = useRef(null);
  const sheetRef = useRef(null);
  /** Page scroll offset captured when the sheet opens (window.scrollY is 0 while body is fixed). */
  const scrollLockYRef = useRef(0);
  const { style: sheetStyle, syncViewport: syncSheetViewport } = useVisualViewportSheetStyle({
    enabled: isOpen,
  });

  // Prevent iOS from scrolling the page behind the sheet when the keyboard opens. Cleanup
  // (fires on any close path — backdrop, escape, outside click, or selection) restores scroll
  // and blurs the search field so iOS doesn't leave the viewport zoomed/shifted.
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    scrollLockYRef.current = scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollLockYRef.current);
      blurMobileSearchInput(searchInputRef);
      nudgeMobileViewportScaleAfterKeyboard();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (sheetRef.current && !sheetRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleSearchFocus = useCallback(() => {
    syncSheetViewport();
    window.setTimeout(syncSheetViewport, 80);
    window.setTimeout(syncSheetViewport, 280);
  }, [syncSheetViewport]);

  const handleSearchChange = (e) => {
    onSearchChange?.(e);
    syncSheetViewport();
  };

  if (!isOpen) return null;

  // Portal to <body> so the sheet always escapes ancestor stacking/transform contexts
  // (e.g. an animated modal), regardless of how deeply nested the trigger is.
  return createPortal(
    <div className={`fixed inset-0 z-[80] ${className}`}>
      <div className="absolute inset-0 bg-black/20" onClick={onClose} aria-hidden />

      <div
        ref={sheetRef}
        className="fixed left-0 right-0 z-[81] flex w-full flex-col rounded-t-xl border border-b-0 border-border-medium bg-surface shadow-xl pb-[env(safe-area-inset-bottom,0px)]"
        style={sheetStyle ?? undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby="player-picker-sheet-title"
      >
        <div className="flex items-center justify-between border-b border-border p-3 shrink-0">
          <div>
            <h3 id="player-picker-sheet-title" className="text-sm font-semibold text-text-primary">
              {title}
            </h3>
            {subtitle && <p className="text-xs text-text-secondary">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 min-h-[44px] min-w-[44px] touch-manipulation text-text-muted transition-colors hover:text-text-primary"
            aria-label="Close"
          >
            <svg className="mx-auto h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-3 pb-2 shrink-0">
          <MobileSearchField
            inputRef={searchInputRef}
            showIcon
            scrollOnFocus={false}
            onFocus={handleSearchFocus}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={handleSearchChange}
            className="form-input w-full py-2 pr-3 border-border shadow-brand-sm transition-[border-color,box-shadow] duration-150 ease-in-out focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>

        {belowSearch ? <div className="px-3 pb-2 shrink-0">{belowSearch}</div> : null}

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pb-3 touch-pan-y">
          {hasResults ? (
            children
          ) : (
            <div className="py-6 text-center">
              <p className="text-xs text-text-muted">{emptyMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PlayerPickerSheet;
